import React, { useState } from "react";
import tailwindClassnames from "../common/TailwindClassnames";
import { useTranslation } from "react-i18next";
import { SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { LocationResponseDto } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";

type LocationsListProps = {
  countries: any;
  listLoading: boolean;
  locations: LocationResponseDto[];
  requestSort: Function;
  onSelectLocation: (location: LocationResponseDto) => void;
  onPageClicked: Function;
  take: number;
  offset: number;
  totalCount: number;
};

const LocationsList: React.FC<LocationsListProps> = ({
  countries,
  listLoading,
  locations,
  requestSort,
  onSelectLocation,
  onPageClicked,
  take,
  offset,
  totalCount,
}) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortByColumn, setSortByColumn] = useState("location_name");

  const handleSort = (column: string) => {
    if (sortByColumn === column) {
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      requestSort(column, newDirection);
    } else {
      setSortByColumn(column);
      setSortDirection("asc");
      requestSort(column, "asc");
    }
  };

  const getArrowDirection = (column: string) => {
    return sortByColumn === column ? (
      sortDirection === "asc" ? (
        <SortedDown />
      ) : (
        <SortedUp />
      )
    ) : (
      <SortingArrows />
    );
  };

  /**
   * Header sort control: renders the column label with the appropriate sort
   * indicator and requests a re-sort on click. `column` is the API sort key.
   */
  const sortableHeader = (label: string, column: string) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 uppercase tracking-wider"
      onClick={() => handleSort(column)}
    >
      {label}
      {getArrowDirection(column)}
    </button>
  );

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr>
            <th className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}>
              {sortableHeader(t("Name"), "location_name")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Country"), "country_id")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Latitude") + " (°)", "latitude")}
            </th>
            <th className={tailwindClassnames.tableCardHeadCell}>
              {sortableHeader(t("Longitude") + " (°)", "longitude")}
            </th>
          </tr>
        </thead>
        <tbody>
          {listLoading ? (
            <tr>
              <td
                colSpan={4}
                className="py-3 px-4 text-left text-sm text-primary-off-black dark:text-primary-off-white transition duration-300"
              >
                {t("Loading")}...
              </td>
            </tr>
          ) : locations.length > 0 ? (
            locations.map((location) => {
              const countryName = countries.find(
                (country: any) => country.code === location.country_id,
              );
              return (
                <tr
                  key={location.location_id}
                  className={tailwindClassnames.tableCardRow}
                  onClick={() => onSelectLocation(location)}
                >
                  <td
                    className={tailwindClassnames.tableCardCellPrimary}
                    title={location.location_name ?? undefined}
                  >
                    {location.location_name}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={countryName?.name || undefined}
                  >
                    {countryName ? countryName.name : ""}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={String(location.latitude ?? "") || undefined}
                  >
                    {location.latitude}
                  </td>
                  <td
                    className={tailwindClassnames.tableCardCell}
                    title={String(location.longitude ?? "") || undefined}
                  >
                    {location.longitude}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={4}
                className="text-center py-3 text-primary-off-black dark:text-primary-off-white transition duration-300"
              >
                {t("No results found")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Pagination
        showText={true}
        handlePageClick={onPageClicked}
        pageCount={Math.max(1, Math.ceil(totalCount / take))}
        resultStart={offset + 1}
        initialPage={Math.floor(offset / take)}
        resultEnd={offset + take < totalCount ? offset + take : totalCount}
        totalCount={totalCount}
        take={take}
        loading={listLoading}
        mainclassname={tailwindClassnames.tableCardFooter}
      />
    </div>
  );
};

export default LocationsList;
